import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Card,
    Colors,
    Heading,
    Icon,
    Text,
} from '@amelio/ui-library';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorFallbackUIProps {
    error: Error | null;
    errorInfo: ErrorInfo | null;
    onReset: () => void;
}

const ErrorFallbackUI = ({ error, errorInfo, onReset }: ErrorFallbackUIProps) => {
    return (
        <div
            className={'flex min-h-screen flex-col items-center justify-center p-6'}
            role={'alert'}
            aria-live={'polite'}
        >
            <Card
                className={'flex w-full max-w-lg flex-col items-center gap-6 text-center'}
                borderRadius={'2xl'}
                padding={'xl'}
            >
                {/* Error icon */}
                <div
                    className={'flex h-20 w-20 items-center justify-center rounded-full bg-red-50'}
                >
                    <Icon
                        name={'essential-danger'}
                        color={Colors.ERROR}
                        size={40}
                    />
                </div>

                {/* Title & message */}
                <div className={'flex flex-col gap-2'}>
                    <Heading level={2}>Something went wrong</Heading>

                    <Text color={Colors.BASE_600}>
                        {error?.message || 'An unexpected error occurred. Please try again.'}
                    </Text>
                </div>

                {/* Dev-only stack trace */}
                {import.meta.env.MODE === 'development' && error && (
                    <details className={'w-full rounded-xl bg-gray-50 p-4 text-left'}>
                        <summary className={'cursor-pointer text-sm font-semibold text-gray-600'}>
                            Error details (development only)
                        </summary>

                        <Text
                            size={'small'}
                            color={Colors.BASE_600}
                            className={'mt-2 font-mono break-all'}
                        >
                            {error.toString()}
                        </Text>

                        {errorInfo && (
                            <pre
                                className={
                                    'mt-2 overflow-auto text-xs whitespace-pre-wrap text-gray-500'
                                }
                            >
                                {errorInfo.componentStack}
                            </pre>
                        )}
                    </details>
                )}

                {/* Actions */}
                <div className={'flex w-full flex-col gap-3 sm:flex-row'}>
                    <ButtonSecondary
                        className={'flex-1'}
                        onClick={() => window.location.reload()}
                    >
                        Reload Page
                    </ButtonSecondary>

                    <ButtonGradientPrimary
                        className={'flex-1'}
                        leftIcon={
                            <Icon
                                name={'arrow-repeat'}
                                color={Colors.WHITE}
                                className={'-mt-0.5 mr-2'}
                                size={'lg'}
                            />
                        }
                        onClick={onReset}
                    >
                        Try Again
                    </ButtonGradientPrimary>
                </div>
            </Card>
        </div>
    );
};

/**
 * Common Error Boundary for the performance-fe micro frontend.
 * Catches JavaScript errors in the child component tree, logs them, and shows a themed fallback.
 *
 * This is required because the host app (Amelio-react) error boundary cannot catch errors
 * thrown inside an isolated micro-frontend instance.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return {
            hasError: true,
            error,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({ error, errorInfo });

        console.error('[performance-fe] ErrorBoundary caught an error:', error, errorInfo);

        this.props.onError?.(error, errorInfo);
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <ErrorFallbackUI
                    error={this.state.error}
                    errorInfo={this.state.errorInfo}
                    onReset={this.handleReset}
                />
            );
        }

        return this.props.children;
    }
}

/**
 * HOC that wraps any component with an ErrorBoundary.
 */
export function withErrorBoundary<P extends object>(
    WrappedComponent: React.ComponentType<P>,
    errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>,
) {
    return function WithErrorBoundaryWrapper(props: P) {
        return (
            <ErrorBoundary {...errorBoundaryProps}>
                <WrappedComponent {...props} />
            </ErrorBoundary>
        );
    };
}
